import { Box, Button, Checkbox, Grid, MenuItem } from '@mui/material';
import { useDialog } from 'app/context';
import { useMedicalCenterDetail } from 'app/hooks';
import { DetailWrapperDiv, TextField, Typography } from 'app/shared';
import { t } from 'i18next';
import { MedicalCenterConfirmDeleteDialog } from './dialog/MedicalCenterConfirmDeleteDialog';
import { IMedicalCenterDeleteSchema } from 'generated/axios';

interface Props {
  id: string;
}

export const MedicalCenterDetailTab = ({ id }: Props) => {
  const { createDialog, closeDialog, createSimpleConfirm } = useDialog();
  const { detail, updateForm, loading, activate, deactivate, deleteMedicalCenter } = useMedicalCenterDetail(id);
  const { submit, bindTextField, bindSelect } = updateForm;

  const deleteConfirm = () =>
    createDialog({
      open: true,
      maxWidth: 'sm',
      fullWidth: true,
      children: (
        <MedicalCenterConfirmDeleteDialog
          medicalCenterEmail={detail?.user_email as string}
          onCancelFn={closeDialog}
          onConfirmFn={(form: IMedicalCenterDeleteSchema) => {
            deleteMedicalCenter(form);
            closeDialog();
          }}
        />
      ),
    });

  const deactivateConfirm = () =>
    createSimpleConfirm({
      title: t('actions.deactivate'),
      message: t('medicalCenter.deactivateConfirmMessage'),
      onConfirm: deactivate,
    });

  return (
    <DetailWrapperDiv>
      <Typography detailTitle>{detail?.name}</Typography>
      <Grid container spacing={4}>
        <Grid size={6}>
          <TextField label={t('medicalCenter.name')} {...bindTextField({ field: 'name' })} />
        </Grid>
        <Grid size={6}>
          <TextField label={t('medicalCenter.email')} {...bindTextField({ field: 'user_email' })} />
        </Grid>
        <Grid size={6}>
          <TextField label={t('medicalCenter.address')} {...bindTextField({ field: 'address' })} />
        </Grid>
        <Grid size={6}>
          <TextField label={t('medicalCenter.vatNumber')} {...bindTextField({ field: 'vat_number' })} />
        </Grid>
        <Grid size={6}>
          <TextField label={t('medicalCenter.phone')} {...bindTextField({ field: 'phone' })} />
        </Grid>
        <Grid size={2}>
          <TextField label={t('common.language')} {...bindSelect({ field: 'language' })}>
            <MenuItem key={'it'} value={'it'}>
              IT
            </MenuItem>
            <MenuItem key={'en'} value={'en'}>
              EN
            </MenuItem>
            <MenuItem key={'fr'} value={'fr'}>
              FR
            </MenuItem>
            <MenuItem key={'de'} value={'de'}>
              DE
            </MenuItem>
            <MenuItem key={'es'} value={'es'}>
              ES
            </MenuItem>
          </TextField>
        </Grid>
        <Grid size={3}>
          <Box padding={1} sx={{ textAlign: 'start', opacity: 0.8, display: 'flex', alignItems: 'center' }}>
            <label>{t('common.isActive')}</label>
            <Checkbox checked={detail?.is_active ?? false} disabled />
          </Box>
        </Grid>
      </Grid>
      <Box padding={4}>
        <Button sx={{ margin: 1 }} variant="contained" loading={loading} onClick={submit}>
          {t('actions.update')}
        </Button>
        {detail?.is_active ? (
          <Button sx={{ margin: 1 }} variant="contained" loading={loading} onClick={deactivateConfirm} color="warning">
            {t('actions.deactivate')}
          </Button>
        ) : (
          <Button sx={{ margin: 1 }} variant="contained" loading={loading} onClick={activate} color="success">
            {t('actions.activate')}
          </Button>
        )}
        <Button sx={{ margin: 1 }} variant="contained" loading={loading} onClick={deleteConfirm} color="error">
          {t('actions.delete')}
        </Button>
      </Box>
    </DetailWrapperDiv>
  );
};
